import { useState } from "react";
import { uploadFile, addSong } from "../firebase/firebase-functions";
import "./addsongform.css";

const AddSongForm = ({ onSongAdded }) => {
  const [formData, setFormData] = useState({
    title: "",
    streamingLinks: {
      spotify: "",
      apple: "",
      amazon: "",
      youtube: "",
    },
  });

  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState("");
  const [coverPreview, setCoverPreview] = useState("");

  const [audioProgress, setAudioProgress] = useState(0);
  const [coverProgress, setCoverProgress] = useState(0);

  const [isUploading, setIsUploading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Audio, 2: Cover, 3: Details
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Handle audio file selection
  const handleAudioChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Validate file type
      if (!file.type.startsWith("audio/")) {
        setError("Please select a valid audio file (MP3, WAV, etc.)");
        return;
      }

      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        setError("Audio file is too large. Maximum size is 50MB.");
        return;
      }

      setAudioFile(file);
      setAudioPreview(file.name);
      setError("");
    }
  };

  // Handle cover art selection
  const handleCoverChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file (JPG, PNG, etc.)");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Image file is too large. Maximum size is 5MB.");
        return;
      }

      setCoverFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);

      setError("");
    }
  };

  // Handle text input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("streaming_")) {
      const platform = name.replace("streaming_", "");
      setFormData((prev) => ({
        ...prev,
        streamingLinks: {
          ...prev.streamingLinks,
          [platform]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate all required fields
    if (!audioFile) {
      setError("Please upload an audio file");
      return;
    }

    if (!coverFile) {
      setError("Please upload cover art");
      return;
    }

    if (!formData.title.trim()) {
      setError("Please enter a song title");
      return;
    }

    setIsUploading(true);

    try {
      // Step 1: Upload audio file
      setSuccess("Uploading audio file...");
      const audioUrl = await uploadFile(audioFile, "music/", setAudioProgress);

      // Step 2: Upload cover art
      setSuccess("Uploading cover art...");
      const coverUrl = await uploadFile(coverFile, "covers/", setCoverProgress);

      // Step 3: Save song data to Firestore
      setSuccess("Saving song information...");
      const songData = {
        title: formData.title,
        audioUrl,
        coverUrl,
        isLatest: false, // New songs aren't latest by default
        streamingLinks: formData.streamingLinks,
      };

      const result = await addSong(songData);

      if (result.success) {
        setSuccess("Song uploaded successfully! 🎉");

        // Reset form
        setFormData({
          title: "",
          streamingLinks: {
            spotify: "",
            apple: "",
            amazon: "",
            youtube: "",
          },
        });
        setAudioFile(null);
        setCoverFile(null);
        setAudioPreview("");
        setCoverPreview("");
        setAudioProgress(0);
        setCoverProgress(0);
        setCurrentStep(1);

        // Call parent callback
        setTimeout(() => {
          onSongAdded();
        }, 1500);
      } else {
        setError(result.error || "Failed to add song");
      }
    } catch (err) {
      setError("Upload failed: " + err.message);
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const canProceedToStep2 = audioFile !== null;
  const canProceedToStep3 = audioFile !== null && coverFile !== null;
  const canSubmit = audioFile && coverFile && formData.title.trim();

  return (
    <div className="add-song-form-wrapper">
      <h3>Add New Song</h3>
      <p className="form-instructions">
        Follow the steps below to upload a new song to your music library.
      </p>

      {/* Progress Steps */}
      <div className="progress-steps">
        <div className={`step ${currentStep >= 1 ? "active" : ""}`}>
          <div className="step-number">1</div>
          <div className="step-label">Upload Audio</div>
        </div>
        <div className={`step ${currentStep >= 2 ? "active" : ""}`}>
          <div className="step-number">2</div>
          <div className="step-label">Upload Cover</div>
        </div>
        <div className={`step ${currentStep >= 3 ? "active" : ""}`}>
          <div className="step-number">3</div>
          <div className="step-label">Song Details</div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="add-song-form">
        {/* Step 1: Audio File */}
        <div className="form-section">
          <h4>Step 1: Upload Audio File</h4>
          <p className="section-description">
            Upload your song in MP3 or WAV format (max 50MB)
          </p>

          <div className="file-input-wrapper">
            <label htmlFor="audio-file" className="file-label">
              <span className="file-icon">🎵</span>
              <span className="file-text">
                {audioPreview || "Choose Audio File"}
              </span>
            </label>
            <input
              type="file"
              id="audio-file"
              accept="audio/*"
              onChange={handleAudioChange}
              disabled={isUploading}
              className="file-input"
            />
          </div>

          {audioFile && (
            <div className="file-preview">
              <span className="preview-icon">✓</span>
              <span>{audioPreview}</span>
            </div>
          )}

          {audioProgress > 0 && audioProgress < 100 && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${audioProgress}%` }}
                ></div>
              </div>
              <span>{audioProgress}%</span>
            </div>
          )}

          {audioFile && (
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="btn-primary btn-next"
              disabled={!canProceedToStep2}
            >
              Next: Upload Cover Art
            </button>
          )}
        </div>

        {/* Step 2: Cover Art */}
        {currentStep >= 2 && (
          <div className="form-section">
            <h4>Step 2: Upload Cover Art</h4>
            <p className="section-description">
              Upload album artwork in JPG or PNG format (max 5MB, square images
              work best)
            </p>

            <div className="file-input-wrapper">
              <label htmlFor="cover-file" className="file-label">
                <span className="file-icon">🖼️</span>
                <span className="file-text">
                  {coverFile ? "Change Cover Art" : "Choose Cover Art"}
                </span>
              </label>
              <input
                type="file"
                id="cover-file"
                accept="image/*"
                onChange={handleCoverChange}
                disabled={isUploading}
                className="file-input"
              />
            </div>

            {coverPreview && (
              <div className="cover-preview">
                <img src={coverPreview} alt="Cover preview" />
              </div>
            )}

            {coverProgress > 0 && coverProgress < 100 && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${coverProgress}%` }}
                  ></div>
                </div>
                <span>{coverProgress}%</span>
              </div>
            )}

            {coverFile && (
              <button
                type="button"
                onClick={() => setCurrentStep(3)}
                className="btn-primary btn-next"
                disabled={!canProceedToStep3}
              >
                Next: Add Song Details
              </button>
            )}
          </div>
        )}

        {/* Step 3: Song Details */}
        {currentStep >= 3 && (
          <div className="form-section">
            <h4>Step 3: Song Details</h4>
            <p className="section-description">
              Add song title and streaming platform links
            </p>

            <div className="form-group">
              <label htmlFor="title" className="form-label required">
                Song Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter song title"
                className="form-input"
                required
                disabled={isUploading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Artist</label>
              <input
                type="text"
                value="Anjali Daswani"
                className="form-input"
                disabled
              />
              <p className="input-hint">Artist name is automatically set</p>
            </div>

            <div className="streaming-links-section">
              <h5>Streaming Platform Links (Optional)</h5>
              <p className="section-description">
                Add links to your song on streaming platforms
              </p>

              <div className="form-group">
                <label htmlFor="streaming_spotify" className="form-label">
                  🎵 Spotify Link
                </label>
                <input
                  type="url"
                  id="streaming_spotify"
                  name="streaming_spotify"
                  value={formData.streamingLinks.spotify}
                  onChange={handleInputChange}
                  placeholder="https://open.spotify.com/track/..."
                  className="form-input"
                  disabled={isUploading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="streaming_apple" className="form-label">
                  🍎 Apple Music Link
                </label>
                <input
                  type="url"
                  id="streaming_apple"
                  name="streaming_apple"
                  value={formData.streamingLinks.apple}
                  onChange={handleInputChange}
                  placeholder="https://music.apple.com/..."
                  className="form-input"
                  disabled={isUploading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="streaming_amazon" className="form-label">
                  📦 Amazon Music Link
                </label>
                <input
                  type="url"
                  id="streaming_amazon"
                  name="streaming_amazon"
                  value={formData.streamingLinks.amazon}
                  onChange={handleInputChange}
                  placeholder="https://music.amazon.com/..."
                  className="form-input"
                  disabled={isUploading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="streaming_youtube" className="form-label">
                  ▶️ YouTube Link
                </label>
                <input
                  type="url"
                  id="streaming_youtube"
                  name="streaming_youtube"
                  value={formData.streamingLinks.youtube}
                  onChange={handleInputChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="form-input"
                  disabled={isUploading}
                />
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Submit Button */}
        {currentStep >= 3 && (
          <button
            type="submit"
            className="btn-primary submit-btn"
            disabled={!canSubmit || isUploading}
          >
            {isUploading ? "Uploading..." : "Add Song to Library"}
          </button>
        )}
      </form>
    </div>
  );
};

export default AddSongForm;
